// Live weather + ground temperature badge for the trail detail page.
// Uses Open-Meteo's key-free forecast endpoint client-side. No backend
// state, no persistence — the component simply fetches on mount for the
// trail's coordinates and renders a small badge with a paw-safety warning
// when the surface temperature crosses the hot-paw (>25°C) or freeze
// (<0°C) thresholds.
//
// Named exports (used by the consolidated Weather Right Now card in
// LocationDetail): `useGroundTemp(lat, lng)` and `classifyPawSafety(surfaceC)`.
import { useEffect, useState } from "react";
import { Sun, Snowflake, PawPrint, ThermometerSun, Loader2 } from "lucide-react";

const HOT_PAW_C = 25;
const FREEZE_C = 0;

export function classifyPawSafety(surfaceC) {
  if (typeof surfaceC !== "number" || Number.isNaN(surfaceC)) {
    return { key: "ok", label: "Ground temp OK", Icon: PawPrint, tone: "neutral" };
  }
  if (surfaceC > HOT_PAW_C) {
    return {
      key: "hot",
      label: "Too hot for paws — walk early or late",
      Icon: Sun,
      tone: "warn",
    };
  }
  if (surfaceC < FREEZE_C) {
    return {
      key: "cold",
      label: "Freezing ground — watch for ice & salt",
      Icon: Snowflake,
      tone: "cold",
    };
  }
  return { key: "ok", label: "Ground temp OK for paws", Icon: PawPrint, tone: "neutral" };
}

// Shared fetcher hook — returns { surfaceC, airC, loading, error }.
// Uses AbortController on unmount so a slow request can't leak into
// the next page.
export function useGroundTemp(lat, lng) {
  const [state, setState] = useState({ surfaceC: null, airC: null, loading: false, error: false });

  useEffect(() => {
    if (typeof lat !== "number" || typeof lng !== "number") return;
    let cancelled = false;
    const ctrl = new AbortController();
    setState((s) => ({ ...s, loading: true, error: false }));
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,surface_temperature&timezone=auto`;
    fetch(url, { signal: ctrl.signal })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error("weather fetch failed")))
      .then((json) => {
        if (cancelled) return;
        const c = json.current || {};
        setState({
          surfaceC: typeof c.surface_temperature === "number" ? c.surface_temperature : null,
          airC: typeof c.temperature_2m === "number" ? c.temperature_2m : null,
          loading: false,
          error: false,
        });
      })
      .catch((e) => {
        if (cancelled || e.name === "AbortError") return;
        setState({ surfaceC: null, airC: null, loading: false, error: true });
      });
    return () => { cancelled = true; ctrl.abort(); };
  }, [lat, lng]);

  return state;
}

const TONE_CLASSES = {
  neutral: "bg-brand-surface border-brand-border text-brand-text",
  warn: "bg-amber-50 border-amber-300 text-amber-900",
  cold: "bg-sky-50 border-sky-300 text-sky-900",
};

export default function PawTempBadge({ lat, lng }) {
  const { surfaceC, airC, loading, error } = useGroundTemp(lat, lng);

  if (typeof lat !== "number" || typeof lng !== "number") return null;

  if (loading && surfaceC == null && airC == null) {
    return (
      <div
        data-testid="paw-temp-badge-loading"
        className="td-card px-3 py-2 flex items-center gap-2 text-xs text-brand-subtle"
      >
        <Loader2 size={14} className="animate-spin" />
        Loading weather…
      </div>
    );
  }

  if (error || (surfaceC == null && airC == null)) return null;

  const { label, Icon, tone, key } = classifyPawSafety(surfaceC);
  const showWarn = key !== "ok";

  return (
    <div
      data-testid="paw-temp-badge"
      data-state={key}
      className={`td-card px-3.5 py-2.5 flex items-center gap-3 border ${TONE_CLASSES[tone]}`}
    >
      <span
        className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${
          tone === "warn" ? "bg-amber-200 text-amber-900" : tone === "cold" ? "bg-sky-200 text-sky-900" : "bg-brand-primary/10 text-brand-primary"
        }`}
      >
        <Icon size={18} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span data-testid="paw-temp-air" className="font-heading text-base text-brand-text leading-tight">
            <ThermometerSun size={13} className="inline -mt-0.5 mr-1 text-brand-subtle" />
            Air {typeof airC === "number" ? `${Math.round(airC)}°C` : "—"}
          </span>
          <span data-testid="paw-temp-ground" className="text-sm text-brand-text/85 leading-tight">
            · Ground {typeof surfaceC === "number" ? `${Math.round(surfaceC)}°C` : "—"}
          </span>
        </div>
        <div
          data-testid="paw-temp-advice"
          className={`text-[11px] leading-snug mt-0.5 ${showWarn ? "font-semibold" : "text-brand-subtle"}`}
        >
          {label}
        </div>
      </div>
    </div>
  );
}
